<script lang="ts">
	import { readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import {
		Button,
		Text,
		Toggle,
		ToggleV2,
		Slider,
		SliderV2,
	} from '@Shelly/shelly-design-system';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import { format } from 'svelte-i18n';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { bindStickyBottomAction } from 'src/helpers/sticky_bottom_action';
	import SettingGuide from '../../SettingGuide.svelte';

	export let device;

	const min = 1;
	const max = 100;

	let loading = false;

	let enabled =
		readDeviceSettings(device.id)?.[`light:${device.channel}`]?.button_presets?.button_doublepush !==
		null;
	let cacheEnabled = enabled;
	let brightness = enabled
		? readDeviceSettings(device.id)?.[`light:${device.channel}`]?.button_presets?.button_doublepush
				?.brightness
		: 100;
	let cacheBrightness = brightness;
	const toggleValue = (e) => {
		enabled = e.target.checked;
	};

	$: isDirty = cacheBrightness !== brightness || cacheEnabled !== enabled;

	const sendReq = async () => {
		if (!isDirty) return;
		loading = true;
		const params = enabled
			? {
					id: device.channel,
					config: {
						button_presets: {
							button_doublepush: {
								key: 'button_doublepush',
								enabled: enabled,
								brightness: brightness,
							},
						},
					},
				}
			: {
					id: device.channel,
					config: {
						button_presets: {
							button_doublepush: null,
						},
					},
				};

		const res: SettingRequest = await gen2Request(device, 'Light.SetConfig', params);

		const response = await onSettingsResponse(res, device);
		if (response.isok) {
			cacheBrightness = brightness;
			cacheEnabled = enabled;
		} else {
			brightness = cacheBrightness;
			enabled = cacheEnabled;
		}
		loading = false;
	};

	const { stickyBottomAction, setStickyBottomAction } = bindStickyBottomAction();

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			setStickyBottomAction({
				onClick: sendReq,
				loading,
				disabled: loading || !isDirty,
				icon: 'fas fa-save',
			});
		} else {
			setStickyBottomAction(null);
		}
	}

	$: ToggleC = $showNewLayout ? ToggleV2 : Toggle;
	$: SliderC = $showNewLayout ? SliderV2 : Slider;

	const a11yProps = {
		sliderValueText: $format('current_brightness_value', {
			values: { value: brightness },
			default: `Current brightness value is ${brightness} percent`,
		}),
		ariaLabel: $format('label_set_brightness', { default: 'Set Brightness' }),
		ariaLabelValue: $format('current_brightness_value', {
			values: { value: brightness },
			default: `Current brightness value is ${brightness} percent`,
		}),
	};
</script>

{#if $showNewLayout}
	<SettingGuide
		text={$format('settings_guide_double_press_preset', {
			default:
				'Set brightness (and color, if this profile supports it) when the wall button is double-pressed.',
		})}
	/>
{:else}
	<Text styleType="body-small" class="mb-s"
		>{$format('description_change_brightness_level', {
			default: 'Change the brightness level on action',
		})}</Text
	>
{/if}

<svelte:component
	this={ToggleC}
	checked={enabled}
	on:click={toggleValue}
	size="m"
	class="mb-s"
	id="toggle-ptlbumy"
>
	{$format('action_on_btn_double_push', {
		default: 'On button double push',
	})}
</svelte:component>

<svelte:component
	this={SliderC}
	id="slider-t0l2xnp"
	{a11yProps}
	class="mb-s"
	bind:value={brightness}
	on:value_change={(event) => {
		brightness = event.detail.value;
	}}
	title="Brightness"
	type={$showNewLayout ? 'horizontal' : 'simple'}
	{min}
	{max}
/>

{#if !$showNewLayout}
	<Button size="m" width="full" {loading} on:click={sendReq}>{$format('button_save')}</Button>
{/if}
